import { TrashIcon } from '@heroicons/react/24/outline';
import { OPERADORAS_PLANO_SAUDE, PLANO_SAUDE_NAO_ADERIR, SEXO_OPCOES } from '../../../models/candidato.opcoes';
import {
  CASADO_OU_UNIAO_ESTAVEL,
  FILHO_ETAPA2_INICIAL,
  INPUT_CLASS,
  LABEL_CLASS,
  RespostasEtapa2Estado,
} from '../candidatoFormExternoEtapa2Page.definitions';
import CampoSimNao from './CampoSimNao';

interface EtapaBeneficiosFamiliaProps {
  respostas: RespostasEtapa2Estado;
  onChange: <K extends keyof RespostasEtapa2Estado>(campo: K, valor: RespostasEtapa2Estado[K]) => void;
}

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500';

/**
 * Dependentes do plano = só cônjuge e filhos/enteados (prática da empresa; enteado entra
 * como filho) — exatamente o que o perfil aceita (HealthPlanFieldset monta a lista a partir
 * do cônjuge + filhos). Por isso não existe mais lista livre de dependentes: aqui só se
 * marca quem entra no plano.
 */
export default function EtapaBeneficiosFamilia({ respostas, onChange }: EtapaBeneficiosFamiliaProps) {
  const aderePlano = Boolean(respostas.planoSaude) && respostas.planoSaude !== PLANO_SAUDE_NAO_ADERIR;
  const temConjuge =
    CASADO_OU_UNIAO_ESTAVEL.includes(respostas.estadoCivil ?? '') && Boolean(respostas.nomeConjuge?.trim());

  const alterarFilho = (index: number, campo: keyof (typeof respostas.filhos)[number], valor: string | boolean) => {
    const novos = [...respostas.filhos];
    novos[index] = { ...novos[index], [campo]: valor };
    onChange('filhos', novos);
  };

  return (
    <>
      <h2 className="mb-6 text-2xl font-bold text-gray-800">Plano de Saúde e Família</h2>
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLASS}>Qual plano de saúde pretende aderir? *</label>
          <select
            className={INPUT_CLASS}
            value={respostas.planoSaude ?? ''}
            onChange={(e) => onChange('planoSaude', e.target.value)}
          >
            <option value="">Selecione</option>
            {OPERADORAS_PLANO_SAUDE.map((opcao) => (
              <option key={opcao}>{opcao}</option>
            ))}
            <option>{PLANO_SAUDE_NAO_ADERIR}</option>
          </select>
        </div>
        {aderePlano && temConjuge && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={respostas.incluirConjugePlano === true}
              onChange={(e) => onChange('incluirConjugePlano', e.target.checked)}
            />
            Incluir {respostas.nomeConjuge?.trim()} (cônjuge) no plano de saúde
          </label>
        )}

        <CampoSimNao
          label="Possui filhos ou enteados?"
          valor={respostas.possuiFilhos}
          onChange={(valor) => onChange('possuiFilhos', valor)}
        />
        {respostas.possuiFilhos && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Enteados entram aqui também, como filhos.</p>
            {respostas.filhos.map((filho, index) => (
              <div key={index} className="space-y-3 rounded-xl border-2 border-gray-100 p-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className={LABEL_CLASS}>Nome completo *</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={filho.nome}
                      onChange={(e) => alterarFilho(index, 'nome', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="mb-3 text-gray-400 hover:text-red-600"
                    title="Remover"
                    onClick={() => onChange('filhos', respostas.filhos.filter((_, i) => i !== index))}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={LABEL_CLASS}>Data de nascimento *</label>
                    <input
                      type="date"
                      className={INPUT_CLASS}
                      value={filho.dataNascimento}
                      onChange={(e) => alterarFilho(index, 'dataNascimento', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Sexo *</label>
                    <select
                      className={INPUT_CLASS}
                      value={filho.sexo ?? ''}
                      onChange={(e) => alterarFilho(index, 'sexo', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {SEXO_OPCOES.map((opcao) => (
                        <option key={opcao}>{opcao}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {aderePlano && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className={CHECKBOX_CLASS}
                      checked={filho.incluirNoPlano === true}
                      onChange={(e) => alterarFilho(index, 'incluirNoPlano', e.target.checked)}
                    />
                    Incluir no plano de saúde
                  </label>
                )}
              </div>
            ))}
            <button
              type="button"
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
              onClick={() => onChange('filhos', [...respostas.filhos, { ...FILHO_ETAPA2_INICIAL }])}
            >
              + Adicionar filho ou enteado
            </button>
          </div>
        )}
      </div>
    </>
  );
}
